import React, { useEffect } from "react";
import { Layout } from "./components/Layout";
import { Home } from "./components/Pages/Home";
import { Route, Switch } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import "./custom.css";
import ChecklistFeaturePage from "./components/Features/Checklist/ChecklistFeaturePage";
import CoursesFeaturePage from "./components/Features/Courses/CoursesFeaturePage";
import UsersFeaturePage from "./components/Features/Users/UsersFeaturePage";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import { Spinner } from "@fluentui/react";
import { loginRequest, interactiveLoginRequest } from "./auth/authClient";
import { InteractionType } from "@azure/msal-browser";
import CreditReportFeaturePage from "./components/Features/CreditReport/CreditReportFeaturePage";
import ContentFeaturePage from "./components/Features/Content/ContentFeaturePage";
import CoBrandSettingsPage from "./components/Features/CoBrandSettings/CoBrandSettingsPage";
import MonthlyTasksRouter from "./components/Features/CoBrandSettings/MonthlyTasks/MonthlyTasksRouter";
import MindfulnessFeaturePage from "./components/Features/Mindfulness/MindfulnessFeaturePage";
import EmailSettings from "./components/Features/CoBrandSettings/EmailSettings/EmailSettings";
import ScholarshipFeaturePage from './components/Features/Scholarships/ScholarshipFeaturePage';
import IconViewerRouter from './components/Features/IconViewer/IconViewerRouter';
import WebinarRouter from './components/Features/Webinars/WebinarsRouter';
import OtherContentPage from './components/Features/OtherContent/OtherContentPage';
import ClientSettingsPage from "./components/Features/ClientsSettings/ClientSettingsPage";
import GoogleAnalyticsPage from "./components/Features/GoogleAnalytics/GoogleAnalyticsPage";
import ErrorFallback from "./components/Util/ErrorFallback";
import AuditRouter from './components/Features/Audits/AuditRouter';
import IntegratedRewardsRouter from "./components/Features/CoBrandSettings/IntegratedRewards/IntegratedRewardsRouter";
import ReportingRouter from './components/Features/Reporting/ReportingRouter';
import QuestionDbPage from "./components/Features/QuestionDb/QuestionDbPage";
import CustomDashboardTasksRouter from "./components/Features/CustomDashboardTasks/CustomDashboardTasksRouter";
import EmlSendersRouter from "./components/Features/EmlSenders/EmlSendersRouter";
import CobrandEmailSendersRouter from "./components/Features/CobrandEmailSenders/CobrandEmailSendersRouter";
import AdminEmailsRouter from "./components/Features/AdminEmails/AdminEmailsRouter";
import EmailUnsubscribesRouter from "./components/Features/EmailUnsubscribes/EmailUnsubscribesRouter";
import CampaignsRouter from "./components/Features/Campaigns/CampaignsRouter";
import StreakRepairPage from "./components/Features/Users/StreakRepair/StreakRepairPage";
import useAuth from "./auth/useAuth";
import AccessDenied from "./components/Util/AccessDenied";

const ROLE_KEYS = {
  CLIENT_SUCCESS_MANAGER: "clientSuccessManager",
  CLIENT_SUCCESS_OPS: "clientSuccessOps",
  COACH: "coach",
  SCHOLARSHIP: "scholarship",
  CONTENT: "content",
  ADMIN: "admin",
};

const ALL_ROLES = Object.values(ROLE_KEYS);

const routes = [
  { path: "/", component: Home, exact: true, roles: ALL_ROLES },
  { path: "/checklist-feature", component: ChecklistFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/course-feature", component: CoursesFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/content-feature", component: ContentFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/credit-report-feature", component: CreditReportFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  // Must precede "/users-feature" so its narrower role list wins in the Switch.
  { path: "/users-feature/streak-repair", component: StreakRepairPage, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/users-feature", component: UsersFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.COACH, ROLE_KEYS.ADMIN] },
  { path: "/other-content", component: OtherContentPage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/client-settings-feature", component: ClientSettingsPage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/cobrand-settings-feature/monthly-tasks", component: MonthlyTasksRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/cobrand-settings-feature", component: CoBrandSettingsPage, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/mindfulness-feature", component: MindfulnessFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/email-settings", component: EmailSettings, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/scholarships-feature", component: ScholarshipFeaturePage, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.SCHOLARSHIP, ROLE_KEYS.ADMIN] },
  { path: "/google-analytics-feature/settings", component: GoogleAnalyticsPage, roles: [ROLE_KEYS.ADMIN] },
  { path: "/icon-viewer-feature", component: IconViewerRouter, roles: [ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/audits", component: AuditRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/webinars-feature", component: WebinarRouter, roles: [ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/integrated-rewards-feature", component: IntegratedRewardsRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/reporting", component: ReportingRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/question-db", component: QuestionDbPage, roles: [ROLE_KEYS.CONTENT, ROLE_KEYS.ADMIN] },
  { path: "/dashboard-feature/custom-tasks", component: CustomDashboardTasksRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/email-senders-feature", component: EmlSendersRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/cobrand-email-senders-feature", component: CobrandEmailSendersRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/email-template-list", component: AdminEmailsRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/email-unsubscribes-report", component: EmailUnsubscribesRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
  { path: "/campaigns-feature", component: CampaignsRouter, roles: [ROLE_KEYS.CLIENT_SUCCESS_MANAGER, ROLE_KEYS.CLIENT_SUCCESS_OPS, ROLE_KEYS.ADMIN] },
];

const RoleRoute = ({ component: Component, roles = [], ...routeProps }) => {
  const { hasAnyRole, isLoadingRoles } = useAuth();
  const isAllowed = roles.length === 0 || hasAnyRole(roles);

  if (roles.length > 0 && isLoadingRoles) {
    return (
      <Route
        {...routeProps}
        render={() => (
          <Spinner
            className="absolute-center"
            label="Loading access permissions..."
          />
        )}
      />
    );
  }

  return (
    <Route
      {...routeProps}
      render={(props) =>
        isAllowed ? <Component {...props} /> : <AccessDenied />
      }
    />
  );
};



const App = () => {
  const { login, error } = useMsalAuthentication(
    InteractionType.Silent,
    loginRequest
  );

  const { inProgress } = useMsal();

  useEffect(() => {
    if (error) {
      // Prompt for account selection rather than silently retrying with a cached session.
      login(InteractionType.Redirect, interactiveLoginRequest);
    }
  }, [error, login]);

  if (inProgress === "login") {
    return (
      <Spinner
        className="absolute-center"
        label="Please wait, we are logging you in!"
      />
    );
  }

  if (inProgress === "logout") {
    return (
      <Spinner
        className="absolute-center"
        label="Please wait, we are logging you out..."
      />
    );
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout>
        <Switch>
          {routes.map((route) => (
            <RoleRoute key={route.path} {...route} />
          ))}
        </Switch>
      </Layout>
    </ErrorBoundary>
  );
};
App.displayName = App.name;

export default App;
